import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { UserViewModel } from '../../../../extViewmodels';
import { canViewTexting } from '../../../../extViewmodels/Utils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { bs } from '../../../styles/styles';
import { SettingCardIcon } from '../../svgs/icons/SettingCardIcon';
import { SettingsGroup, SettingsGroupIconBadge } from '../SettingsGroup';
import { SettingsGroupIcon } from '../SettingsGroupIcon';
import { EmailAndCalendarSettings } from './EmailAndCalendarSettings';
import { EmailExecAssistantSettings } from './EmailExecAssistantSettings';
import { EmailSignatureSettings } from './EmailSignatureSettings';
import { HandwrittenCardSettings } from './HandwrittenCardSettings';
import { ImportContactsSettings } from './ImportContactsSettings';
import { MeetingPrepSettings } from './MeetingPrepSettings';
import { NotificationsSettings } from './NotificationSettings';
import { ProfileSettings } from './ProfileSettings';
import { SecuritySettings } from './SecuritySettings';
import { SocialMediaSettings } from './SocialMediaSettings';
import { styleSheet } from './styles';
import { TextingSettings } from './TextingSettings';
import { ZoomConnection } from '../ZoomConnection';
import { MailSparkleIcon } from '@WebComponents/svgs/icons/MailSparkleIcon';

interface IProps {
	sectionToExpand?: string;
}

function _PersonalSettings({ sectionToExpand }: IProps) {
	const userSession = useUserSession();

	const [userViewModel] = React.useState(new UserViewModel(userSession, userSession.user));
	const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({});

	// Initialize expanded sections based on URL parameter
	React.useEffect(() => {
		if (sectionToExpand) {
			setExpandedSections({ [sectionToExpand]: true });
		}
	}, [sectionToExpand]);

	const handleToggleExpansion = (sectionKey: string) => (expanded: boolean) => {
		setExpandedSections(prev => ({ ...prev, [sectionKey]: expanded }));
	};

	return (
		<div className={css(styleSheet.root)}>
			<SettingsGroup
				/* className={showingTimeZoneOptions ? css(styleSheet.showingTzOptions) : undefined} */
				expanded={expandedSections['profile'] ?? sectionToExpand === 'profile'}
				icon={<SettingsGroupIcon type='UserProfile' />}
				name='Profile'
				onToggleExpansion={handleToggleExpansion('profile')}
				description='Change your name, contact info, and time zone.'
			>
				<ProfileSettings
					expanded={expandedSections['profile'] ?? sectionToExpand === 'profile'}
					onToggleExpansion={handleToggleExpansion('profile')}
				/>
			</SettingsGroup>
			<SettingsGroup
				expanded={expandedSections['email-and-calendar'] ?? sectionToExpand === 'email-and-calendar'}
				onToggleExpansion={handleToggleExpansion('email-and-calendar')}
				className={css(bs.relative)}
				description={`Adjust email frequency${
					userSession.user.emailProviderFeatures.calendar.canViewHistoryAll
						? ', recent meetings, and calendar'
						: ' and recent meetings'
				} settings.`}
				icon={<SettingsGroupIcon type='KitAndEmail' />}
				name={`Keep in Touch, Email${userSession.user.emailProviderFeatures.calendar.canViewHistoryAll ? ' & Calendar' : ''} Settings`}
			>
				<EmailAndCalendarSettings
					userViewModel={userViewModel}
					onCancel={() => handleToggleExpansion('email-and-calendar')(false)}
				/>
			</SettingsGroup>
			<SettingsGroup
				expanded={expandedSections['security'] ?? sectionToExpand === 'security'}
				onToggleExpansion={handleToggleExpansion('security')}
				description={`Change your password${userSession?.account?.requireSecondAuthFactor ? ' and manage your two-factor authentication settings.' : '.'}`}
				icon={<SettingsGroupIcon type='Password' />}
				name='Security'
			>
				<SecuritySettings
					expanded={sectionToExpand === 'security'}
					onCancel={() => handleToggleExpansion('security')(false)}
				/>
			</SettingsGroup>
			{userSession.account.features.socialMedia?.enabled &&
			userSession.user?.userGroupPermissions?.featurePermissions?.socialMedia?.send ? (
				<SettingsGroup
					expanded={expandedSections['social-media'] ?? sectionToExpand === 'social-media'}
					onToggleExpansion={handleToggleExpansion('social-media')}
					description='Add or remove your social media accounts.'
					icon={<SettingsGroupIcon type='SocialMedia' />}
					name='Social Media'
				>
					<SocialMediaSettings />
				</SettingsGroup>
			) : null}
			{userSession.account.features.handwrittenCards?.enabled ? (
				<SettingsGroup
					description='Setup your card with personalization writing style, signature and logo.'
					expanded={expandedSections['handwritten-cards'] ?? sectionToExpand === 'handwritten-cards'}
					onToggleExpansion={handleToggleExpansion('handwritten-cards')}
					icon={<SettingCardIcon />}
					name='Cards Setting'
				>
					<HandwrittenCardSettings onCancel={() => handleToggleExpansion('handwritten-cards')(false)} />
				</SettingsGroup>
			) : null}
			{userSession.account.features.meetingPrep?.enabled ? (
				<SettingsGroup
					description='Get an email brief for your upcoming meetings.'
					expanded={expandedSections['meeting-prep'] ?? sectionToExpand === 'meeting-prep'}
					icon={<SettingsGroupIcon type='ScheduleMeeting' />}
					name='Meeting Prep'
					onToggleExpansion={handleToggleExpansion('meeting-prep')}
				>
					<MeetingPrepSettings onCancel={() => handleToggleExpansion('meeting-prep')(false)} />
				</SettingsGroup>
			) : null}
			{userSession.account.features.zoom?.enabled ? (
				<SettingsGroup
					description='Connect your Zoom account to generate unique Zoom links for your meetings.'
					expanded={expandedSections['zoom'] ?? sectionToExpand === 'zoom'}
					icon={<SettingsGroupIcon type='ScheduleMeeting' />}
					name='Zoom'
					onToggleExpansion={handleToggleExpansion('zoom')}
				>
					<ZoomConnection connectReturnPath='/settings/personal/zoom' showHeading={false} />
				</SettingsGroup>
			) : null}
			{userSession.account?.features?.emailExec?.available ? (
				<SettingsGroup
					description='Get a daily brief of what needs your attention across your inbox.'
					expanded={expandedSections['email-exec-assistant'] ?? sectionToExpand === 'email-exec-assistant'}
					icon={
						<SettingsGroupIconBadge>
							<MailSparkleIcon />
						</SettingsGroupIconBadge>
					}
					name='Email Exec Assistant'
					onToggleExpansion={handleToggleExpansion('email-exec-assistant')}
				>
					<EmailExecAssistantSettings />
				</SettingsGroup>
			) : null}
			<SettingsGroup
				description='Manage your email signature.'
				expanded={expandedSections['email-signature'] ?? sectionToExpand === 'email-signature'}
				icon={<SettingsGroupIcon type='EmailSignature' />}
				name='Email Signature'
				onToggleExpansion={handleToggleExpansion('email-signature')}
			>
				<EmailSignatureSettings onCancel={() => handleToggleExpansion('email-signature')(false)} />
			</SettingsGroup>
			<SettingsGroup
				expanded={expandedSections['import-contacts'] ?? sectionToExpand === 'import-contacts'}
				onToggleExpansion={handleToggleExpansion('import-contacts')}
				className={`${css(bs.relative)} import-contacts-settings`}
				description='Automatically build your contact list.'
				icon={<SettingsGroupIcon type='ImportContacts' />}
				name='Import Contacts'
			>
				<ImportContactsSettings
					userViewModel={userViewModel}
					onCancel={() => handleToggleExpansion('import-contacts')(false)}
				/>
			</SettingsGroup>
			{canViewTexting(userSession) ? (
				<SettingsGroup
					description='Manage number and call forwarding.'
					expanded={expandedSections['texting'] ?? sectionToExpand === 'texting'}
					icon={<SettingsGroupIcon type='Texting' />}
					name='Texting'
					onToggleExpansion={handleToggleExpansion('texting')}
				>
					<TextingSettings onCancel={() => handleToggleExpansion('texting')(false)} />
				</SettingsGroup>
			) : null}
			<SettingsGroup
				expanded={expandedSections['notifications'] ?? sectionToExpand === 'notifications'}
				description='Manage how you want to be notified when people read your email'
				name='Notifications'
				icon={<SettingsGroupIcon type='Notifications' />}
				onToggleExpansion={handleToggleExpansion('notifications')}
			>
				<NotificationsSettings
					userViewModel={userViewModel}
					onCancel={() => handleToggleExpansion('notifications')(false)}
				/>
			</SettingsGroup>
		</div>
	);
}

export const PersonalSettings = observer(_PersonalSettings);
